import { useMemo, useState } from "react";
import { Link, useRoute } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar } from "@/components/mnf/UI";
import SeasonChip from "@/components/mnf/SeasonChip";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  autoMacros,
  ratiosToMacros,
  fixedKcal,
  tdee,
  seasonAdjustment,
  type ActivityLevel,
  type GoalDirection,
  type GoalPace,
  type MacroMode,
  type MacroProfile,
} from "@/lib/store";

/**
 * /coach/macros/:menteeId - Coach Kiley sets and locks a sister's targets.
 * She can choose Auto (computed from a working profile), Ratios, or Fixed,
 * preview the plan, add a warm note, and lock it for the season.
 */
export default function CoachMacros() {
  const [, params] = useRoute<{ menteeId: string }>("/coach/macros/:menteeId");
  const menteeId = params?.menteeId ?? "";

  const mentee = useStoreQuery(() => queries.sisterById(menteeId));
  const profile = useStoreQuery(() => queries.macroProfile(menteeId));
  const plan = useStoreQuery(() => queries.macroPlan(menteeId));
  const season = useStoreQuery(() => queries.seasonState(menteeId));
  const setClearance = useStoreMutation(mutations.setMovementClearance);
  const seasonAdj = season ? seasonAdjustment(season) : null;

  if (!mentee) {
    return (
      <>
        <ScreenHeader
          variant="forest"
          eyebrow="Coach view"
          title={<>Sister <span className="italic">not found</span></>}
          trailing={
            <Link href="/coach">
              <span className="inline-flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 999, background: "rgba(245,240,232,0.14)", color: "#F5F0E8", cursor: "pointer" }}>
                <Icon name="arrow_back" size={18} weight={500} />
              </span>
            </Link>
          }
        />
      </>
    );
  }

  const workingProfile: MacroProfile = profile ?? {
    userId: menteeId,
    seasonKind: "none",
    ageYears: 32,
    heightIn: 65,
    weightLb: 150,
    activity: "moderate",
    direction: "maintain",
    pace: "gentle",
    updatedAt: Date.now(),
  };

  return (
    <>
      <ScreenHeader
        variant="forest"
        eyebrow="Coach view"
        title={<>Macros for <span className="italic">{mentee.name.split(" ")[0]}</span></>}
        subtitle="Set her plan with care. She'll see the targets and your note when she opens her macros."
        trailing={
          <Link href="/coach">
            <span className="inline-flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 999, background: "rgba(245,240,232,0.14)", color: "#F5F0E8", cursor: "pointer" }}>
              <Icon name="arrow_back" size={18} weight={500} />
            </span>
          </Link>
        }
      />

      <div className="px-5 -mt-3 space-y-4 pb-6">
        {/* Sister header */}
        <GlassCard padding={16}>
          <div className="flex items-center gap-3">
            <Avatar initials={mentee.initials} size={48} ringColor="rgba(229,185,151,0.65)" />
            <div className="flex-1 min-w-0">
              <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>{mentee.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <PillTag tone="gold">{mentee.tier}</PillTag>
                <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
                  {mentee.streak}-day streak
                </span>
              </div>
            </div>
          </div>

          {plan && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              <Tile label="kcal" value={plan.kcal} accent="var(--mnf-f)" />
              <Tile label="P" value={plan.proteinG} suffix="g" accent="var(--mnf-fiber)" />
              <Tile label="C" value={plan.carbsG} suffix="g" accent="var(--mnf-purple)" />
              <Tile label="F" value={plan.fatG} suffix="g" accent="var(--mnf-gold)" />
            </div>
          )}

          {plan?.locked && plan.source === "coach" && (
            <div className="mt-3 flex items-center gap-2">
              <PillTag tone="forest">Currently locked by you</PillTag>
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => mutations.releaseCoachOverride(menteeId)}
              >
                Release
              </Btn>
            </div>
          )}
        </GlassCard>

        {/* Season + movement clearance */}
        {season && season.kind !== "none" && (
          <GlassCard padding={16}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Her season</p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <SeasonChip season={season} size="md" testId="chip-coach-mentee-season" />
                </div>
                {seasonAdj && (
                  <p className="mnf-body mt-2.5" style={{ fontSize: 12.5, color: "var(--mnf-body)", lineHeight: 1.45 }}>
                    {seasonAdj.label}: {seasonAdj.kcalDelta >= 0 ? "+" : ""}{seasonAdj.kcalDelta} kcal, protein floor {seasonAdj.proteinPerKg} g per kg.
                  </p>
                )}
              </div>
            </div>
            {season.kind === "postpartum" && (
              <div className="mt-3 flex items-center justify-between gap-3 p-3 rounded-2xl" style={{ background: "rgba(168,212,184,0.16)" }}>
                <div className="flex-1 min-w-0">
                  <p className="mnf-body" style={{ fontSize: 12.5, fontWeight: 700, color: "var(--mnf-fd)" }}>
                    Cleared for movement
                  </p>
                  <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 2 }}>
                    Toggle on once she's been cleared by her provider.
                  </p>
                </div>
                <button
                  onClick={() => setClearance(menteeId, !season.clearedForMovement)}
                  data-testid="toggle-coach-clearance"
                  aria-pressed={season.clearedForMovement}
                  className="hover-elevate"
                  style={{
                    width: 44, height: 26, borderRadius: 999,
                    background: season.clearedForMovement ? "var(--mnf-f)" : "rgba(30,74,53,0.18)",
                    border: "none", cursor: "pointer", position: "relative",
                    transition: "background 160ms ease",
                  }}
                >
                  <span style={{
                    position: "absolute", top: 3, left: season.clearedForMovement ? 21 : 3,
                    width: 20, height: 20, borderRadius: 999, background: "#F5F0E8",
                    transition: "left 160ms ease",
                  }} />
                </button>
              </div>
            )}
          </GlassCard>
        )}

        <CoachEditor
          menteeId={menteeId}
          firstName={mentee.name.split(" ")[0]}
          workingProfile={workingProfile}
          existingMode={plan?.mode ?? "auto"}
          existingPlan={plan}
        />

        {/* Pastoral guidance */}
        <GlassCard organic padding={20} style={{ textAlign: "center" }}>
          <p className="mnf-serif italic" style={{ fontSize: 14, color: "var(--mnf-fd)", lineHeight: 1.55 }}>
            "Be shepherds of God's flock that is under your care, watching over them, not because you must, but because you are willing."
          </p>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)", marginTop: 8 }}>1 Peter 5:2</p>
        </GlassCard>
      </div>
    </>
  );
}

/* ----------------------------- Coach editor ------------------------------ */

function CoachEditor({
  menteeId, firstName, workingProfile, existingMode, existingPlan,
}: {
  menteeId: string;
  firstName: string;
  workingProfile: MacroProfile;
  existingMode: MacroMode;
  existingPlan: import("@/lib/store").MacroPlan | null;
}) {
  const [tab, setTab] = useState<MacroMode>(existingMode);
  const [note, setNote] = useState<string>(
    existingPlan?.coachNote ?? `${firstName}, walking with you on these for this season. We can revisit anytime.`,
  );

  // Auto editor state (working profile snapshot, doesn't save profile to her until lock-in)
  const [age, setAge] = useState(workingProfile.ageYears);
  const [heightIn, setHeightIn] = useState(workingProfile.heightIn);
  const [weightLb, setWeightLb] = useState(workingProfile.weightLb);
  const [activity, setActivity] = useState<ActivityLevel>(workingProfile.activity);
  const [direction, setDirection] = useState<GoalDirection>(workingProfile.direction);
  const [pace, setPace] = useState<GoalPace>(workingProfile.pace);

  const livePreviewProfile: MacroProfile = useMemo(
    () => ({ ...workingProfile, ageYears: age, heightIn, weightLb, activity, direction, pace }),
    [workingProfile, age, heightIn, weightLb, activity, direction, pace],
  );
  const auto = useMemo(() => autoMacros(livePreviewProfile), [livePreviewProfile]);

  // Ratios state
  const [kcal, setKcal] = useState(existingPlan?.kcal ?? auto.kcal);
  const [pPct, setPPct] = useState(existingPlan?.ratios.p ?? auto.ratios.p);
  const [cPct, setCPct] = useState(existingPlan?.ratios.c ?? auto.ratios.c);
  const fPct = Math.max(0, 100 - pPct - cPct);
  const ratiosResult = useMemo(() => ratiosToMacros(kcal, { p: pPct, c: cPct, f: fPct }), [kcal, pPct, cPct, fPct]);

  // Fixed state
  const [pG, setPG] = useState(existingPlan?.proteinG ?? auto.proteinG);
  const [cG, setCG] = useState(existingPlan?.carbsG ?? auto.carbsG);
  const [fG, setFG] = useState(existingPlan?.fatG ?? auto.fatG);
  const fixedTotal = fixedKcal(pG, cG, fG);

  const lockIn = () => {
    if (tab === "auto") {
      mutations.saveMacroProfile({
        userId: menteeId,
        seasonKind: workingProfile.seasonKind,
        ageYears: age, heightIn, weightLb, activity, direction, pace,
      });
      mutations.coachOverrideMacros({
        userId: menteeId,
        mode: "auto",
        kcal: auto.kcal,
        proteinG: auto.proteinG,
        carbsG: auto.carbsG,
        fatG: auto.fatG,
        ratios: auto.ratios,
        coachNote: note.trim() || `Walking with you on these for now.`,
      });
    } else if (tab === "ratios") {
      mutations.coachOverrideMacros({
        userId: menteeId,
        mode: "ratios",
        kcal,
        proteinG: ratiosResult.proteinG,
        carbsG: ratiosResult.carbsG,
        fatG: ratiosResult.fatG,
        ratios: { p: pPct, c: cPct, f: fPct },
        coachNote: note.trim() || `Walking with you on these for now.`,
      });
    } else {
      const total = fixedTotal;
      const ratios = total > 0
        ? { p: Math.round((pG * 4 / total) * 100), c: Math.round((cG * 4 / total) * 100), f: 0 }
        : { p: 0, c: 0, f: 0 };
      ratios.f = 100 - ratios.p - ratios.c;
      mutations.coachOverrideMacros({
        userId: menteeId,
        mode: "fixed",
        kcal: total,
        proteinG: pG,
        carbsG: cG,
        fatG: fG,
        ratios,
        coachNote: note.trim() || `Walking with you on these for now.`,
      });
    }
  };

  return (
    <GlassCard padding={18}>
      <div className="mb-3">
        <p className="mnf-eyebrow mb-1">Set her plan</p>
        <h3 className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>
          Choose how you'll calculate
        </h3>
      </div>

      <div className="flex items-center gap-1 p-1 mb-4" style={{ background: "rgba(30,74,53,0.06)", borderRadius: 999 }}>
        {(["auto", "ratios", "fixed"] as const).map((m) => {
          const active = tab === m;
          return (
            <button
              key={m}
              onClick={() => setTab(m)}
              className="flex-1 mnf-body transition-all"
              style={{
                padding: "8px 10px", borderRadius: 999,
                background: active ? "var(--mnf-fd)" : "transparent",
                color: active ? "#F5F0E8" : "var(--mnf-f)",
                fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer",
                textTransform: "capitalize", letterSpacing: "0.04em",
              }}
            >
              {m}
            </button>
          );
        })}
      </div>

      {tab === "auto" && (
        <div className="space-y-3">
          <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
            Tune her stats and the season she's in. We compute kcal and macros from there.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <NumField label="Age" value={age} onChange={setAge} suffix="yr" />
            <NumField label="Height" value={heightIn} onChange={setHeightIn} suffix="in" />
            <NumField label="Weight" value={weightLb} onChange={setWeightLb} suffix="lb" />
          </div>
          <SegRow
            label="Activity"
            value={activity}
            onChange={(v) => setActivity(v as ActivityLevel)}
            options={[
              { value: "sedentary", label: "Sed" },
              { value: "light", label: "Light" },
              { value: "moderate", label: "Mod" },
              { value: "active", label: "Active" },
              { value: "very_active", label: "Very" },
            ]}
          />
          <SegRow
            label="Goal"
            value={direction}
            onChange={(v) => setDirection(v as GoalDirection)}
            options={[
              { value: "release", label: "Release" },
              { value: "maintain", label: "Maintain" },
              { value: "build", label: "Build" },
            ]}
          />
          {direction !== "maintain" && (
            <SegRow
              label="Pace"
              value={pace}
              onChange={(v) => setPace(v as GoalPace)}
              options={[
                { value: "gentle", label: "Gentle" },
                { value: "steady", label: "Steady" },
                { value: "strong", label: "Strong" },
              ]}
            />
          )}
          <PreviewBlock kcal={auto.kcal} p={auto.proteinG} c={auto.carbsG} f={auto.fatG} sub={`TDEE ${tdee(livePreviewProfile)}`} />
        </div>
      )}

      {tab === "ratios" && (
        <div className="space-y-3">
          <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
            Set her energy target and how the kcal divide between protein, carbs, and fat.
          </p>
          <NumField label="Energy" value={kcal} onChange={setKcal} suffix="kcal" min={1000} max={4500} step={10} />
          <RatioRow color="var(--mnf-fiber)" label="Protein" pct={pPct} onChange={(v) => setPPct(Math.max(0, Math.min(100 - cPct, v)))} grams={ratiosResult.proteinG} />
          <RatioRow color="var(--mnf-purple)" label="Carbs" pct={cPct} onChange={(v) => setCPct(Math.max(0, Math.min(100 - pPct, v)))} grams={ratiosResult.carbsG} />
          <RatioRow color="var(--mnf-gold)" label="Fat" pct={fPct} onChange={() => { /* derived */ }} grams={ratiosResult.fatG} disabled />
          <PreviewBlock kcal={kcal} p={ratiosResult.proteinG} c={ratiosResult.carbsG} f={ratiosResult.fatG} sub={`Split ${pPct}/${cPct}/${fPct}%`} />
        </div>
      )}

      {tab === "fixed" && (
        <div className="space-y-3">
          <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
            Set fixed grams. Energy total updates as you go.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <NumField label="Protein" value={pG} onChange={setPG} suffix="g" accent="var(--mnf-fiber)" />
            <NumField label="Carbs" value={cG} onChange={setCG} suffix="g" accent="var(--mnf-purple)" />
            <NumField label="Fat" value={fG} onChange={setFG} suffix="g" accent="var(--mnf-gold)" />
          </div>
          <PreviewBlock kcal={fixedTotal} p={pG} c={cG} f={fG} sub="Fixed totals" />
        </div>
      )}

      {/* Note + lock */}
      <div className="mt-4">
        <label className="flex flex-col">
          <span className="mnf-eyebrow mb-1.5">Note from you</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="mnf-body"
            style={{
              background: "#fff",
              border: "1px solid rgba(30,74,53,0.12)",
              borderRadius: 14, padding: "10px 12px",
              fontSize: 13, color: "var(--mnf-fd)", outline: "none",
              resize: "vertical",
            }}
          />
        </label>
        <p className="mnf-body italic" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 6, lineHeight: 1.5 }}>
          She'll see this on her macros screen. Locking means she can't edit until you release.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <Btn variant="primary" block onClick={lockIn}>Lock in plan</Btn>
        </div>
      </div>
    </GlassCard>
  );
}

/* ----------------------------- Atoms ----------------------------------- */

function PreviewBlock({ kcal, p, c, f, sub }: { kcal: number; p: number; c: number; f: number; sub?: string }) {
  return (
    <div
      style={{
        background: "linear-gradient(135deg, rgba(168,212,184,0.18) 0%, rgba(192,120,74,0.10) 100%)",
        borderRadius: 16, padding: 14,
      }}
    >
      <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-f)" }}>Preview</p>
      <div className="grid grid-cols-4 gap-2">
        <Tile label="kcal" value={kcal} accent="var(--mnf-f)" />
        <Tile label="P" value={p} suffix="g" accent="var(--mnf-fiber)" />
        <Tile label="C" value={c} suffix="g" accent="var(--mnf-purple)" />
        <Tile label="F" value={f} suffix="g" accent="var(--mnf-gold)" />
      </div>
      {sub && (
        <p className="mnf-body italic" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 8 }}>
          {sub}
        </p>
      )}
    </div>
  );
}

function Tile({ label, value, suffix, accent }: { label: string; value: number; suffix?: string; accent: string }) {
  return (
    <div
      style={{
        background: "rgba(30,74,53,0.04)",
        borderRadius: 14, padding: "8px 6px",
        textAlign: "center",
        border: "1px solid rgba(30,74,53,0.08)",
      }}
    >
      <p className="mnf-eyebrow" style={{ color: accent, fontSize: 9.5 }}>{label}</p>
      <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 2 }}>
        {value}{suffix && <span className="mnf-body" style={{ fontSize: 10, color: "var(--mnf-tg)", fontWeight: 500 }}>{suffix}</span>}
      </p>
    </div>
  );
}

function NumField({
  label, value, onChange, suffix, min, max, step, accent,
}: {
  label: string; value: number; onChange: (n: number) => void;
  suffix?: string; min?: number; max?: number; step?: number; accent?: string;
}) {
  return (
    <label
      className="flex flex-col"
      style={{
        background: "#fff",
        border: `1px solid ${accent ? "rgba(30,74,53,0.18)" : "rgba(30,74,53,0.12)"}`,
        borderRadius: 14, padding: "8px 12px",
      }}
    >
      <span className="mnf-eyebrow" style={{ color: accent ?? "var(--mnf-tg)", fontSize: 9.5 }}>{label}</span>
      <span className="flex items-baseline gap-1">
        <input
          type="number" inputMode="numeric"
          min={min} max={max} step={step ?? 1}
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => onChange(Number(e.target.value))}
          className="mnf-serif"
          style={{
            width: "100%", border: "none", outline: "none", background: "transparent",
            fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", padding: 0,
          }}
        />
        {suffix && <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>{suffix}</span>}
      </span>
    </label>
  );
}

function SegRow({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div>
      <p className="mnf-eyebrow mb-1.5">{label}</p>
      <div className="flex items-center gap-1 flex-wrap">
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(o.value)}
              className="mnf-body transition-all"
              style={{
                padding: "7px 12px", borderRadius: 999,
                background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                color: active ? "#F5F0E8" : "var(--mnf-f)",
                border: "none", fontSize: 11.5, fontWeight: 600, cursor: "pointer",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RatioRow({
  color, label, pct, onChange, grams, disabled,
}: {
  color: string; label: string; pct: number; onChange: (v: number) => void;
  grams: number; disabled?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span style={{ width: 10, height: 10, borderRadius: 999, background: color, display: "inline-block" }} aria-hidden />
          <span className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>{label}</span>
          <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>{pct}%</span>
        </div>
        <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>{grams}g</span>
      </div>
      <input
        type="range" min={0} max={100} step={1} value={pct}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
        style={{ accentColor: color }}
      />
    </div>
  );
}
